<template>
  <Combobox
    v-model="selectedOrgs"
    class="w-full h-[48px] px-[18px] rounded-sm leading-[18px] text-gray-800 bg-gray-800/5 tablet-h:bg-transparent border border-transparent tablet-h:border-gray-800/25 relative font-primary text-[14px]"
    :class="inputClasses"
    v-slot="{ addItem }"
  >
    <ComboboxItem
      v-if="selectedOrgs"
      class="w-full h-full font-primary pt-1.5 py-2 flex items-center justify-between"
    >
      <div>
        <div class="text-[11px] opacity-50 font-medium">Organization name</div>
        <div class="flex items-center">
          <Avatar :src="selectedOrgs[0].avatarUrl" :size="18" class="mr-1" />
          <div>{{ selectedOrgs[0].name }}</div>
        </div>
      </div>

      <Icon
        :size="18"
        name="Plus"
        class="rotate-45 stroke-gray-800 opacity-30 mt-1 cursor-pointer"
        @click="handleResetUser"
      />
    </ComboboxItem>
    <ComboboxInput
      v-if="!selectedOrgs"
      class="font-primary bg-transparent outline-hidden w-full h-full py-[15px] text-gray-800 placeholder:text-gray-800/50 capitalize"
      :placeholder="props.label"
      :disabled="props.disabled"
      ref="input"
      @change="handleChange"
      @keydown.enter="handleEnter(addItem)"
      @click="handleInputClick"
    />
    <div
      v-if="showResults"
      class="max-h-[200px] overflow-scroll w-full absolute left-0 bg-white rounded-[8px] [box-shadow:0_0_8px_0_rgb(0_0_0_/_10%),_0_2px_3px_0_rgb(0_0_0_/_20%)] z-10"
      ref="resultsRef"
      :style="styles"
      @scroll="handleScroll"
    >
      <div v-if="loading && !users.length">
        <div
          v-for="n in 3"
          :key="n"
          class="w-full h-[48px] px-4 flex items-center first:rounded-tl-[8px] first:rounded-tr-[8px] last:rounded-bl-[8px] last:rounded-br-[8px]"
        >
          <Skeleton class="w-[30px] h-[30px] rounded-full mr-2.5" />
          <div>
            <Skeleton class="w-[200px] h-[14px] mb-1.5" />
            <Skeleton class="w-[140px] h-[12px]" />
          </div>
        </div>
      </div>
      <ComboboxResult
        v-else
        v-for="user in users"
        :key="user.id"
        :value="user"
        v-slot="{ focused }"
      >
        <div
          class="w-full h-[48px] px-4 font-secondary font-medium flex items-center cursor-pointer hover:bg-gray-800/10"
          :class="getActiveClasses(focused)"
        >
          <Avatar
            v-if="user.avatarUrl"
            :size="30"
            :src="user.avatarUrl"
            class="mr-2.5"
          />
          <Icon v-else name="User" :size="30" class="stroke-gray-800 mr-2.5" />
          <div v-truncate class="w-full">
            <div data-truncate-target class="text-[14px]">
              {{ user.name }}
            </div>
            <div data-truncate-target class="text-[12px] opacity-50">
              Managed by {{ (user as Organization).owner?.name }}
            </div>
          </div>
        </div>
      </ComboboxResult>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import {
  Combobox,
  ComboboxInput,
  ComboboxItem,
  ComboboxResult,
} from '@/shared/components/Combobox'
import {
  EntityType,
  useSearchEntitiesLazyQuery,
  type Entity,
  type Organization,
} from '@/shared/graphql/legacy/generated'
import { EMAIL_REGEX } from '@/shared/utils/string'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Avatar, Skeleton, Icon } from '@dacr/ui'

const props = defineProps<{
  modelValue?: Entity
  label: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', payload?: Entity): void
  (e: 'update:query', payload: string): void
}>()

const query = ref('')

const variables = ref({
  query,
  filterBy: EntityType.Organization,
})

const { result, loading, load, fetchMore } =
  useSearchEntitiesLazyQuery(variables)

const selectedOrgs = ref<Entity[]>()
const resultsRef = ref<HTMLElement>()
const showResults = ref(false)
const input = ref<InstanceType<typeof ComboboxInput>>()

watch(selectedOrgs, () => {
  if (!selectedOrgs.value?.[0]) return

  showResults.value = false
  emit('update:query', '')
  emit('update:modelValue', selectedOrgs.value[0])
})

const styles = computed(() => {
  const boundingClientRect = resultsRef.value?.getBoundingClientRect()

  const topOfContextMenu = boundingClientRect?.top || 0
  const bottomOfContextMenu = topOfContextMenu + 200

  if (bottomOfContextMenu >= window.innerHeight) {
    return {
      bottom: 'calc(0% + 40px)',
    }
  }

  return {
    top: '105%',
  }
})

const getActiveClasses = (active: boolean) => {
  return {
    outline: active,
    'outline-green-700': active,
    'outline-[3px]': active,
    'outline-offset-[-3px]': active,
  }
}

const inputClasses = computed(() => ({
  ...(props.disabled && {
    'pointer-events-none': true,
  }),
}))

const handleChange = (value: string) => {
  query.value = value

  emit('update:query', value)

  showResults.value = !!query.value.length

  load()
}

const handleEnter = (addItem: (value: unknown) => void) => {
  if (!EMAIL_REGEX.test(query.value)) {
    return
  }

  addItem({
    id: query.value,
    name: query.value,
  } as Entity)
}

const handleResetUser = () => {
  selectedOrgs.value = undefined
}

const users = computed(() => {
  const filteredUsers = (result.value?.searchEntities.entities || []).filter(
    (user) => user.id !== selectedOrgs.value?.[0].id
  )

  return filteredUsers
})

const fetchNextPage = () => {
  if (result.value?.searchEntities.next && !loading.value) {
    fetchMore({
      variables: {
        next: result.value?.searchEntities.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query',
          searchEntities: {
            ...previousData.searchEntities,
            entities: [
              ...previousData.searchEntities.entities,
              ...newData.searchEntities.entities,
            ],
            next: newData.searchEntities.next,
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight

  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

const handleInputClick = () => {
  if (!query.value) return

  showResults.value = true
}

const handleOutsideClick = (event: MouseEvent) => {
  if (!input.value?.$el.contains(event.target)) {
    showResults.value = false
  }
}

onMounted(() => {
  window.addEventListener('click', handleOutsideClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleOutsideClick)
})
</script>
